import type { Difficulty, MCQQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

type QType = 'elapsed_min' | 'elapsed_hr_min' | 'end_time' | 'start_time';

/**
 * N-3-17：時間長度計算（加減）——三下第七章。
 * 和 math.time.elapsed（三上）區別：三下側重跨小時計算與「時」「分」混合。
 *
 * difficulty 1：同一小時內（分鐘加減 < 60）
 * difficulty 2：跨小時（分鐘加減可進退位）
 * difficulty 3：時數差 + 分鐘差（HH:MM 格式）
 */
export function generateTimeDuration(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  const qTypes: QType[] = difficulty === 1
    ? ['elapsed_min', 'end_time']
    : difficulty === 2
    ? ['elapsed_hr_min', 'end_time', 'start_time']
    : ['elapsed_hr_min', 'start_time', 'elapsed_hr_min'];

  const qType = qTypes[randInt(rand, 0, qTypes.length - 1)]!;

  let prompt: string;
  let correct: string;
  let options: string[];
  let explanation: string;

  if (qType === 'elapsed_min') {
    // Same hour: elapsed minutes
    const startH = randInt(rand, 8, 14);
    const startM = randInt(rand, 0, 5) * 5; // 0,5,10,15,20,25
    const endM = startM + randInt(rand, 1, 6) * 5;   // strictly < 60
    const elapsed = endM - startM;

    correct = `${elapsed} 分鐘`;
    prompt = `從 ${startH}:${pad2(startM)} 到 ${startH}:${pad2(endM)}，經過幾分鐘？`;
    const w1 = `${elapsed + 5} 分鐘`;
    const w2 = `${elapsed - 5 > 0 ? elapsed - 5 : elapsed + 10} 分鐘`;
    const w3 = `${elapsed + 10} 分鐘`;
    options = [correct, w1, w2, w3];
    explanation = `${pad2(endM)} − ${pad2(startM)} = ${elapsed} 分鐘。`;

  } else if (qType === 'elapsed_hr_min') {
    // Cross-hour: how long between two times (1–3 hours apart)
    const startH = randInt(rand, 8, 14);
    const startM = randInt(rand, 0, 3) * 15; // 0,15,30,45
    const durationH = randInt(rand, 1, 3);
    const durationM = randInt(rand, 0, 3) * 15;
    const endTotal = startH * 60 + startM + durationH * 60 + durationM;
    const endH = Math.floor(endTotal / 60) % 24;
    const endM = endTotal % 60;

    const durationLabel = durationM > 0
      ? `${durationH} 小時 ${durationM} 分鐘`
      : `${durationH} 小時`;

    correct = durationLabel;
    prompt = `從 ${startH}:${pad2(startM)} 到 ${endH}:${pad2(endM)}，共經過多久？`;
    const w1 = durationM > 0 ? `${durationH} 小時 ${durationM + 5} 分鐘` : `${durationH + 1} 小時`;
    const w2 = `${durationH + 1} 小時`;
    const w3 = durationM > 0 ? `${durationH - 1 > 0 ? durationH - 1 : durationH} 小時 ${durationM} 分鐘` : `${durationH - 1 > 0 ? durationH - 1 : durationH + 2} 小時`;
    options = [correct, w1, w2, w3];
    explanation = `終止時刻 ${endH}:${pad2(endM)} − 開始時刻 ${startH}:${pad2(startM)} = ${durationLabel}。`;

  } else if (qType === 'end_time') {
    // Given start + duration → end time
    const startH = randInt(rand, 8, 15);
    const startM = randInt(rand, 0, 3) * 15;
    const addM = randInt(rand, 1, 8) * 15;  // 15,30,45,60,...120
    const endTotal = startH * 60 + startM + addM;
    const endH = Math.floor(endTotal / 60) % 24;
    const endM = endTotal % 60;

    const addLabel = addM >= 60
      ? `${Math.floor(addM / 60)} 小時${addM % 60 > 0 ? ` ${addM % 60} 分鐘` : ''}`
      : `${addM} 分鐘`;

    correct = `${endH}:${pad2(endM)}`;
    prompt = `${startH}:${pad2(startM)} 再過 ${addLabel}，是幾時幾分？`;
    const w1 = `${endH}:${pad2((endM + 15) % 60)}`;
    const w2 = `${(endH + 1) % 24}:${pad2(endM)}`;
    const w3 = `${endH}:${pad2(endM > 0 ? endM - 15 : 45)}`;
    options = [correct, w1, w2, w3];
    explanation = `${startH}:${pad2(startM)} + ${addLabel} = ${endH}:${pad2(endM)}。`;

  } else {
    // start_time: given end + duration → find start
    const endH = randInt(rand, 9, 17);
    const endM = randInt(rand, 0, 3) * 15;
    const subM = randInt(rand, 1, 8) * 15;
    const startTotal = endH * 60 + endM - subM;
    const startH = Math.floor(startTotal / 60);
    const startM = startTotal % 60;

    const subLabel = subM >= 60
      ? `${Math.floor(subM / 60)} 小時${subM % 60 > 0 ? ` ${subM % 60} 分鐘` : ''}`
      : `${subM} 分鐘`;

    correct = `${startH}:${pad2(startM)}`;
    prompt = `某活動結束時是 ${endH}:${pad2(endM)}，活動持續了 ${subLabel}，活動是幾時幾分開始的？`;
    const w1 = `${startH}:${pad2((startM + 15) % 60)}`;
    const w2 = `${startH + 1 < 24 ? startH + 1 : startH}:${pad2(startM)}`;
    const w3 = `${startH}:${pad2(startM > 0 ? startM - 15 : 45)}`;
    options = [correct, w1, w2, w3];
    explanation = `開始時刻 = 結束時刻 − 持續時間 = ${endH}:${pad2(endM)} − ${subLabel} = ${correct}。`;
  }

  // Ensure 4 unique options
  const seen = new Set<string>();
  const uniqueOpts: string[] = [];
  for (const o of options) {
    if (!seen.has(o)) { seen.add(o); uniqueOpts.push(o); }
  }
  while (uniqueOpts.length < 4) {
    const extra = `${randInt(rand, 1, 60)} 分鐘`;
    if (!seen.has(extra) && extra !== correct) { seen.add(extra); uniqueOpts.push(extra); }
  }
  if (!uniqueOpts.includes(correct)) uniqueOpts[0] = correct;

  const shuffled = uniqueOpts.slice(0, 4).sort(
    (x, y) => mulberry32(seed + x.length * 17)() - mulberry32(seed + y.length * 17)(),
  );

  return {
    kind: 'mcq',
    prompt,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation,
    kp: 'math.time.duration',
  };
}
